import { Injectable, OnDestroy } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  AppState,
  selectConfiguration,
  selectRouter,
  selectTicketFilters,
} from '../../core.state';
import { Store } from '@ngrx/store';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { EMPTY, of, Subject } from 'rxjs';

import { TicketClient } from '../../../client/ticket';
import {
  callSyndicateTicketList,
  callTicketList,
  failTicketList,
  listLoadMore,
  loadMoreDispatcher,
  loadSyndicateTicketList,
  loadTicketList,
  loadTicketListFromRoute,
  successList,
  syndicateListLoadMore,
} from '../tickets.actions';
import { selectPoolFilters } from '../../pool/pool.selectors';
import { SyndicatePortionClient } from '../../../client/syndicate_portions';
import { setWebsocketEvent } from '../../coreWebsockets/coreWebsockets.actions';
import { ws } from './../tickets.reducer';

interface WebsocketEvent {
  event: string;
  data?: {
    object_id?: number;
    pool_id?: number;
  };
}

@Injectable()
export class TicketsListEffects implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  /**
   * URL → Store bridge effect.
   *
   * Dispatched by the Smart Container (TicketsListView) whenever the activated
   * route params or query params change. Replaces the old `routernavigationTicketsList`
   * effect that listened to the global `routerNavigatedAction` and matched URLs via
   * brittle string-includes checks.
   *
   * Enriches `pool_types` from the NgRx pool filters when missing in the URL, so that
   * the URL stays clean while the API still receives the correct pool type filter.
   */
  loadFromRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTicketListFromRoute),
      withLatestFrom(
        this.store.select(selectPoolFilters),
        this.store.select(selectConfiguration),
      ),
      takeUntil(this.destroy$),
      exhaustMap(([{ listType, filters, contentType }, poolFilters, config]) => {
        // Enrich pool_types from NgRx pool filters when URL does not carry it.
        // Resolution order:
        //  1. contentType from URL (?content_type=RACING) → use RACING pool bucket
        //  2. config.displayState.view (app-level config view, e.g. 'ALL') → fallback
        const resolvedFilters = { ...filters };
        if (!resolvedFilters.pool_types && poolFilters) {
          const viewKey =
            contentType ?? config?.displayState?.view;
          if (viewKey) {
            const defaultPoolTypes = (poolFilters as Record<string, any>)[
              viewKey
            ]?.LIVE?.all;
            if (defaultPoolTypes) {
              resolvedFilters.pool_types = defaultPoolTypes;
            }
          }
        }

        switch (listType) {
          case 'syndicates':
            return of(
              loadSyndicateTicketList({ model: { filters: resolvedFilters as any } })
            );
          default:
            return of(
              loadTicketList({ model: { filters: resolvedFilters as any } })
            );
        }
      })
    ),
    { dispatch: true }
  );

  loadMoreDispatcher = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMoreDispatcher),
      withLatestFrom(this.store.select(selectRouter)),
      takeUntil(this.destroy$),
      exhaustMap(([, router]) => {
        if (!router) return EMPTY;

        // /tickets/list/group load-more is handled exclusively by GroupTicketsFeatureModule
        if (router.state.url.includes('/tickets/list/group')) {
          return EMPTY;
        }

        const type = router.state.url.includes('/tickets/list/syndicates')
          ? 'syndicates'
          : 'single';

        return type === 'syndicates'
          ? of(syndicateListLoadMore())
          : of(listLoadMore());
      })
    )
  );

  /** Load-more effects */
  listLoadMore = createEffect(
    () =>
      this.actions$.pipe(
        ofType(listLoadMore),
        withLatestFrom(this.store.select(selectTicketFilters)),
        takeUntil(this.destroy$),
        exhaustMap(([, pagination]) =>
          of(callTicketList({ model: { page: pagination.page } }))
        )
      ),
    { dispatch: true }
  );

  syndicateListLoadMore = createEffect(
    () =>
      this.actions$.pipe(
        ofType(syndicateListLoadMore),
        withLatestFrom(this.store.select(selectTicketFilters)),
        takeUntil(this.destroy$),
        exhaustMap(([, pagination]) =>
          of(callSyndicateTicketList({ model: { page: pagination.page } }))
        )
      ),
    { dispatch: true }
  );

  /**
   * switchMap cancels previous in-flight requests when filters change rapidly
   * (e.g., user clicking through filter options quickly).
   */
  loadList = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadTicketList),
        withLatestFrom(this.store.select(selectTicketFilters)),
        takeUntil(this.destroy$),
        switchMap(([, pagination]) =>
          of(
            callTicketList({
              model: { filters: pagination.filters },
            })
          )
        )
      ),
    { dispatch: true }
  );

  loadSyndicateTicketList = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadSyndicateTicketList),
        withLatestFrom(this.store.select(selectTicketFilters)),
        takeUntil(this.destroy$),
        switchMap(([, pagination]) =>
          of(
            callSyndicateTicketList({
              model: { filters: pagination.filters },
            })
          )
        )
      ),
    { dispatch: true }
  );

  callList = createEffect(
    () =>
      this.actions$.pipe(
        ofType(callTicketList),
        withLatestFrom(this.store.select(selectTicketFilters)),
        takeUntil(this.destroy$),
        switchMap(([, filters]) =>
          this.ticketClient.list(filters).pipe(
            map((data) => successList({ response: data })),
            catchError((error) => {
              console.error(
                '[Tickets List Effects] Failed to load ticket list:',
                error
              );
              return of(failTicketList({ error }));
            })
          )
        )
      ),
    { dispatch: true }
  );

  callSyndicateTicketList = createEffect(
    () =>
      this.actions$.pipe(
        ofType(callSyndicateTicketList),
        withLatestFrom(this.store.select(selectTicketFilters)),
        takeUntil(this.destroy$),
        switchMap(([, filters]) =>
          this.syndicatePortionClient.list(filters).pipe(
            map((data) => successList({ response: data })),
            catchError((error) => {
              console.error(
                '[Tickets List Effects] Failed to load syndicate ticket list:',
                error
              );
              return of(failTicketList({ error }));
            })
          )
        )
      ),
    { dispatch: true }
  );

  /**
   * WebSocket live-update effect.
   *
   * Intentionally still reads the router URL and stored pagination — not `loadFromRoute$`
   * — because WS reloads are triggered by backend push events, not by URL navigation.
   * The user is already on the page; the URL has NOT changed. Using the stored pagination
   * is the correct approach here.
   *
   * See DECISIONS.md for rationale.
   */
  setWebsocketEvent = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setWebsocketEvent),
        filter((propVal: WebsocketEvent) =>
          ws.RELOAD_EVENTS.concat(Object.keys(ws)).includes(propVal.event)
        ),
        withLatestFrom(
          this.store.select(selectRouter),
          this.store.select(selectTicketFilters)
        ),
        switchMap(([event, routerState, pagination]) => {
          if (this.isOfferEvent(event.event)) {
            if (routerState?.state?.url?.includes('/tickets/list/single')) {
              return of(callTicketList({ model: { filters: pagination.filters } }));
            }
            if (routerState?.state?.url?.includes('/tickets/list/syndicates')) {
              return of(callSyndicateTicketList({ model: { page: pagination.page } }));
            }
          }
          return EMPTY;
        }),
        catchError((error) => {
          console.error('Websocket effect error:', error);
          return EMPTY;
        })
      ),
    { dispatch: true }
  );

  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    public ticketClient: TicketClient,
    public syndicatePortionClient: SyndicatePortionClient
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isOfferEvent(event: string): boolean {
    return ['USER_NEW_OFFER', 'USER_REMOVE_OFFER'].includes(event);
  }
}
